// Табът „Замени" на складовата карта: всяка замяна на продукта в периода — кога,
// „⇄ заместил Кафе A" (изписан ВМЕСТО него) или „⇄ заместен с Кафе B" (липсата
// му е покрита), колко от този продукт срещу колко от другия и от кой документ.
// Тук е и получателят при разнасяне, който в „Движения" няма собствен ред.
import { useTranslation } from "react-i18next";
import type { StockCardSubstitution } from "../../../../api/inventoryStockCard";
import { formatQty, useUnitDecimals } from "../../components/qty";
import { fmtDateTime } from "../../components/dateTime";
import { REPORT_HEAD_CLASS, REPORT_HEADER_ROW_CLASS, REPORT_ROW_CLASS } from "../components/reportListModel";
import { formatCardDate, isOpenableDocument } from "./stockCardModel";

const ROW_GRID =
  "grid grid-cols-[46px_minmax(0,1fr)_76px] items-center gap-x-2.5 px-4 @min-[560px]:grid-cols-[50px_minmax(0,1.4fr)_84px_minmax(0,1fr)_minmax(0,1fr)]";
const WIDE_ONLY = "hidden @min-[560px]:block";

type Props = {
  substitutions: StockCardSubstitution[];
  decimals: number;
  unit: string | null;
  dateFormat: string;
  onOpenDocument: (item: StockCardSubstitution) => void;
};

export const StockCardSubstitutions = ({ substitutions, decimals, unit, dateFormat, onOpenDocument }: Props) => {
  const { t } = useTranslation();
  const decimalsOf = useUnitDecimals();
  const replacedCount = substitutions.filter((item) => item.role === "donor").length;
  const replacedByCount = substitutions.length - replacedCount;

  if (!substitutions.length) {
    return (
      <div className="px-4 py-8 text-center text-[13px] leading-[1.4] text-body">
        {t("inventoryPage.scSubsEmpty")}
      </div>
    );
  }

  const documentLabelOf = (item: StockCardSubstitution) =>
    t("inventoryPage.scDocNumber", {
      label: t(`inventoryPage.scDoc_${item.documentType}`),
      number: item.documentNumber,
    });

  return (
    <div className="@container flex min-h-0 flex-1 flex-col overflow-auto">
      <p className="m-0 border-b border-shade-2 px-4 py-2.5 text-[12.5px] leading-[1.4] text-body">
        {[
          replacedCount ? t("inventoryPage.scSubsCountReplaced", { count: replacedCount }) : null,
          replacedByCount ? t("inventoryPage.scSubsCountReplacedBy", { count: replacedByCount }) : null,
        ]
          .filter(Boolean)
          .join(" · ")}
      </p>
      <div className={`${ROW_GRID} ${REPORT_HEADER_ROW_CLASS} sticky top-0 z-[1] bg-panel`}>
        <span className={REPORT_HEAD_CLASS}>{t("inventoryPage.colDate")}</span>
        <span className={REPORT_HEAD_CLASS}>{t("inventoryPage.scColSubstitution")}</span>
        <span className={`${REPORT_HEAD_CLASS} text-right`}>{t("inventoryPage.colQty")}</span>
        <span className={`${REPORT_HEAD_CLASS} ${WIDE_ONLY}`}>{t("inventoryPage.colDocument")}</span>
        <span className={`${REPORT_HEAD_CLASS} ${WIDE_ONLY}`}>{t("inventoryPage.scColUser")}</span>
      </div>
      {substitutions.map((item, index) => {
        const counterpartQty = `${formatQty(item.counterpartQuantity, decimalsOf(item.counterpartProductId))}${
          item.counterpartUnit ? ` ${item.counterpartUnit}` : ""
        }`;
        const document = isOpenableDocument(item.documentType) ? (
          <button
            type="button"
            onClick={() => onOpenDocument(item)}
            className="min-w-0 cursor-pointer truncate border-none bg-transparent p-0 text-left text-[12.5px] leading-[1.3] text-ink underline decoration-shade-4 underline-offset-2 outline-none hover:text-brand-deep-ink hover:decoration-brand focus-visible:shadow-[0_0_0_2px_var(--color-brand)]"
          >
            {documentLabelOf(item)}
          </button>
        ) : (
          <span className="truncate text-[12.5px] text-ink">{documentLabelOf(item)}</span>
        );
        return (
          <div key={`${item.movementId}-${index}`} className={`${ROW_GRID} ${REPORT_ROW_CLASS}`}>
            <span className="font-sheet text-xs text-body" title={fmtDateTime(item.date)}>
              {formatCardDate(item.date, dateFormat)}
            </span>
            {/* широка карта: „покрити …" е приглушено на същия ред (един ред на запис) */}
            <span className="flex min-w-0 flex-col gap-px @min-[560px]:flex-row @min-[560px]:items-baseline @min-[560px]:gap-2">
              <span className="min-w-0 truncate text-[13px] font-medium leading-[1.3] text-brand-deep-ink">
                {t(item.role === "donor" ? "inventoryPage.scBadgeReplaced" : "inventoryPage.scBadgeReplacedBy", {
                  name: item.counterpartProductName,
                })}
              </span>
              <span className="min-w-0 truncate text-[11.5px] leading-[1.3] text-muted @min-[560px]:shrink-[2]">
                {t(item.role === "donor" ? "inventoryPage.scSubsCovered" : "inventoryPage.scSubsTaken", {
                  qty: counterpartQty,
                })}
              </span>
              {/* тясна карта: документът слиза тук */}
              <span className="flex min-w-0 @min-[560px]:hidden">{document}</span>
            </span>
            <span
              className={`text-right font-sheet tabular-nums ${
                item.role === "donor" ? "text-danger-ink" : "text-ok-deep"
              }`}
            >
              {/* донорът е изписан (минус); на получателя е покрита нужда — без знак */}
              {item.role === "donor" ? "−" : ""}
              {formatQty(item.quantity, decimals)}
              {unit && <span className="ml-1 font-normal text-muted">{unit}</span>}
            </span>
            <span className={`${WIDE_ONLY} min-w-0 truncate`}>{document}</span>
            <span className={`${WIDE_ONLY} truncate text-xs text-body`}>{item.createdByName ?? ""}</span>
          </div>
        );
      })}
    </div>
  );
};

export default StockCardSubstitutions;
